import { useMemo, useState } from "react";
import { Button, Table } from "antd";
import { DataKitMenuDto, MenuUsageDto } from "../../../../domain/DataKitDto";
import { DataKitMenuDrawer } from "./DataKitMenuDrawer";
import "./DataKitMenusTab.scss";

interface Row {
  menu: DataKitMenuDto;
  kitItems: number;
  kitDesigns: number;
}

interface Props {
  menus: DataKitMenuDto[];
  usage: MenuUsageDto[];
  canWrite: boolean;
  onChanged: () => void;
}

/**
 * The kit's Menus tab: a **usage view** over the account's menu list, not a second list.
 *
 * Every kit sees the same menus — the difference between two kits' tabs is the counts, which is the
 * point of the view. There is deliberately no creator here; menus are made on the account's own
 * Menus tab. Edit opens the same drawer, so a note or a rename made from a kit still lands on the
 * one shared record.
 */
export function DataKitMenusTab({ menus, usage, canWrite, onChanged }: Props) {
  const [editing, setEditing] = useState<DataKitMenuDto | null>(null);

  const usageByMenuId = useMemo(() => {
    const map = new Map<string, MenuUsageDto>();
    usage.forEach((u) => {
      if (u.menuId) map.set(u.menuId, u);
    });
    return map;
  }, [usage]);

  const rows: Row[] = useMemo(
    () =>
      menus.map((menu) => {
        const u = menu.id ? usageByMenuId.get(menu.id) : undefined;
        return {
          menu,
          kitItems: u?.itemCount ?? 0,
          kitDesigns: u?.designCount ?? 0,
        };
      }),
    [menus, usageByMenuId]
  );

  /** Designs on a menu that place nothing from this kit — worth saying rather than showing 0. */
  const unlabelled = useMemo(() => usage.find((u) => !u.menuId), [usage]);

  const columns = [
    {
      title: "Menu",
      key: "menu",
      render: (_: unknown, row: Row) => (
        <div className="menus-usage-name-cell">
          <div className="menus-usage-name">{row.menu.name}</div>
          {row.menu.note && <div className="menus-usage-note">{row.menu.note}</div>}
        </div>
      ),
      sorter: (a: Row, b: Row) =>
        a.menu.name.localeCompare(b.menu.name, undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
    },
    {
      title: "Items from this kit",
      dataIndex: "kitItems",
      key: "kitItems",
      width: 170,
      align: "center" as const,
      render: (count: number) =>
        count > 0 ? count : <span className="menus-usage-empty">--</span>,
    },
    {
      title: "Designs",
      dataIndex: "kitDesigns",
      key: "kitDesigns",
      width: 120,
      align: "center" as const,
      render: (count: number) =>
        count > 0 ? count : <span className="menus-usage-empty">--</span>,
    },
    {
      title: "",
      key: "action",
      width: 90,
      align: "right" as const,
      render: (_: unknown, row: Row) =>
        canWrite ? (
          <Button className="menus-usage-edit-btn" size="small" onClick={() => setEditing(row.menu)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="data-kit-menus-tab">
      <p className="data-kit-menus-hint">
        Account menus, with how much of this kit each one uses. Menus are created and deleted on the
        account&apos;s own Menus tab.
      </p>

      <Table
        className="data-kit-menus-table"
        dataSource={rows}
        columns={columns}
        rowKey={(row) => row.menu.id || row.menu.name}
        pagination={false}
        locale={{ emptyText: "No menus on this account yet" }}
      />

      {unlabelled && unlabelled.designCount > 0 && (
        <p className="menus-usage-unlabelled">
          {unlabelled.designCount} design{unlabelled.designCount === 1 ? "" : "s"} using this kit
          {unlabelled.designCount === 1 ? " is" : " are"} not on any menu yet.
        </p>
      )}

      {editing && (
        <DataKitMenuDrawer
          menu={editing}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
